<template>
  <div class="sso-authorize">
    <!-- 背景装饰光斑 -->
    <div class="bg-blob blob-1" />
    <div class="bg-blob blob-2" />
    <div class="bg-blob blob-3" />

    <div class="sso-card" :class="`is-${status}`">
      <!-- 图标区：加载 / 成功 / 失败 -->
      <div class="sso-icon-wrap">
        <transition name="icon-swap" mode="out-in">
          <!-- 加载中：旋转环 + 环绕光点 -->
          <div v-if="status === 'loading'" key="loading" class="spinner-wrap">
            <span class="spinner-ring" />
            <span class="spinner-orbit" />
            <span class="pulse-halo" />
          </div>

          <!-- 成功：SVG 勾号描边动画 -->
          <svg v-else-if="status === 'success'" key="success" class="check" viewBox="0 0 52 52">
            <circle class="check-circle" cx="26" cy="26" r="25" fill="none" />
            <path class="check-mark" fill="none" d="M14 27l7 7 16-16" />
          </svg>

          <!-- 失败：抖动叉号 -->
          <svg v-else key="error" class="cross" viewBox="0 0 52 52">
            <circle class="cross-circle" cx="26" cy="26" r="25" fill="none" />
            <path class="cross-line" fill="none" d="M17 17l18 18 M35 17l-18 18" />
          </svg>
        </transition>
      </div>

      <!-- 文案 -->
      <transition name="fade-up" mode="out-in">
        <p :key="message" class="sso-message">{{ message }}</p>
      </transition>

      <!-- 加载中跳动的省略点 -->
      <div v-if="status === 'loading'" class="dots">
        <span /><span /><span />
      </div>

      <!-- 进度条 -->
      <div class="sso-progress">
        <i :class="`bar bar-${status}`" />
      </div>

      <!-- 失败时的重试入口 -->
      <transition name="fade-up">
        <el-button
          v-if="status === 'error'"
          size="small"
          type="primary"
          plain
          class="sso-retry"
          @click="retry"
        >
          {{ businessLoginUrl ? '返回业务系统登录页' : '返回登录页' }}
        </el-button>
      </transition>
    </div>
  </div>
</template>

<script>
import { authorize } from '../api/sso';

export default {
  name: 'Sso',
  data() {
    return {
      status: 'loading', // loading | success | error
      message: '正在处理，请稍候...',
      // 本次授权的业务系统回调地址，失败时据此推导它的登录页
      redirectUri: '',
      timer: null
    };
  },
  computed: {
    /**
     * 回调地址 -> 业务系统登录页
     *   http://biz.com/ssocallback    => http://biz.com/login
     *   http://biz.com/#/ssocallback  => http://biz.com/#/login
     * 拿不到回调地址时返回空串，此时退回 SSO 自己的登录页
     */
    businessLoginUrl() {
      if (!this.redirectUri) return '';
      return this.redirectUri.replace(/ssocallback/gi, 'login');
    }
  },
  beforeDestroy() {
    // 组件销毁时清掉延时跳转，避免在已离开的页面触发跳走
    if (this.timer) clearTimeout(this.timer);
  },
  async created() {
    const { app_id, state } = this.$route.query;

    if (!app_id || !state) {
      this.fail('参数错误：缺少 app_id 或 state');
      return;
    }
    // this.redirectUri = redirect_uri;

    const sessionToken = localStorage.getItem('sso_token');
    if (!sessionToken) {
      this.$router.replace({
        path: '/login',
        query: { returnUrl: this.$route.fullPath }
      });
      return;
    }

    try {
      const { data } = await authorize({ sessionToken, app_id, state });
      this.redirectUri = data.redirect_uri
      if(data.code === 403){
        this.fail(data.message);
        return
      }
      const target =
      this.redirectUri +
      `?sso_token=${encodeURIComponent(data.sso_token)}` +
      `&state=${encodeURIComponent(state)}`;
      this.succeed('授权成功，正在跳转...');
      // 留出时间让勾号动画播完再跳转
      this.timer = setTimeout(() => {
        window.location.href = target;
      }, 1000);
    } catch (e) {
      this.fail(e.message || '授权失败，请重新登录');
    }
  },
  methods: {
    succeed(msg) {
      this.status = 'success';
      this.message = msg;
    },
    fail(msg) {
      this.status = 'error';
      this.message = msg;
    },
    retry() {
      // 能推导出业务系统登录页就跳过去（跨站，走浏览器）
      if (this.businessLoginUrl) {
        window.location.href = this.businessLoginUrl;
        return;
      }
      // 否则回 SSO 登录页，带上 returnUrl 以便登录后继续
      this.$router.replace({
        path: '/login'
        // query: { returnUrl: this.$route.fullPath }
      });
    }
  }
};
</script>

<style scoped>
/* ---------- 整页居中 ---------- */
.sso-authorize {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* ---------- 背景光斑 ---------- */
.bg-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.5;
  pointer-events: none;
}
.blob-1 {
  width: 320px;
  height: 320px;
  top: -80px;
  left: -60px;
  background: #7f9cf5;
  animation: float-a 12s ease-in-out infinite;
}
.blob-2 {
  width: 380px;
  height: 380px;
  right: -100px;
  bottom: -120px;
  background: #b794f4;
  animation: float-b 15s ease-in-out infinite;
}
.blob-3 {
  width: 220px;
  height: 220px;
  left: 45%;
  bottom: 10%;
  background: #f687b3;
  opacity: 0.35;
  animation: float-a 18s ease-in-out infinite reverse;
}
@keyframes float-a {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(40px, 30px) scale(1.12); }
}
@keyframes float-b {
  0%, 100% { transform: translate(0, 0) scale(1.08); }
  50% { transform: translate(-50px, -25px) scale(0.92); }
}

/* ---------- 卡片 ---------- */
.sso-card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 360px;
  padding: 44px 32px 30px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 20px 50px rgba(29, 38, 113, 0.28);
  animation: card-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: box-shadow 0.35s ease;
}
.sso-card.is-success { box-shadow: 0 20px 50px rgba(56, 161, 105, 0.3); }
.sso-card.is-error { box-shadow: 0 20px 50px rgba(229, 62, 62, 0.3); }
.sso-card.is-error { animation: card-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) both, shake 0.5s 0.35s; }

@keyframes card-in {
  from { opacity: 0; transform: translateY(24px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-9px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(3px); }
}

/* ---------- 图标区 ---------- */
.sso-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  margin-bottom: 22px;
}

.spinner-wrap {
  position: relative;
  width: 56px;
  height: 56px;
}
.spinner-ring {
  position: absolute;
  inset: 0;
  border: 3px solid rgba(102, 126, 234, 0.15);
  border-top-color: #667eea;
  border-right-color: #764ba2;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
.spinner-orbit {
  position: absolute;
  top: -3px;
  left: 50%;
  width: 9px;
  height: 9px;
  margin-left: -4.5px;
  border-radius: 50%;
  background: #764ba2;
  transform-origin: 4.5px 31px;
  animation: spin 1.4s linear infinite;
}
.pulse-halo {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  border: 2px solid rgba(102, 126, 234, 0.4);
  animation: pulse 1.8s ease-out infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes pulse {
  0% { opacity: 0.7; transform: scale(0.85); }
  70%, 100% { opacity: 0; transform: scale(1.35); }
}

/* ---------- 成功 / 失败 SVG ---------- */
.check, .cross { width: 62px; height: 62px; }
.check-circle, .check-mark {
  stroke: #38a169;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.check-circle { stroke-dasharray: 166; stroke-dashoffset: 166; animation: stroke 0.5s cubic-bezier(0.65, 0, 0.45, 1) forwards; }
.check-mark { stroke-width: 4; stroke-dasharray: 48; stroke-dashoffset: 48; animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.45s forwards; }

.cross-circle, .cross-line {
  stroke: #e53e3e;
  stroke-width: 3;
  stroke-linecap: round;
}
.cross-circle { stroke-dasharray: 166; stroke-dashoffset: 166; animation: stroke 0.45s cubic-bezier(0.65, 0, 0.45, 1) forwards; }
.cross-line { stroke-dasharray: 60; stroke-dashoffset: 60; animation: stroke 0.25s cubic-bezier(0.65, 0, 0.45, 1) 0.4s forwards; }

@keyframes stroke { to { stroke-dashoffset: 0; } }

/* ---------- 文案 ---------- */
.sso-message {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  text-align: center;
  color: #4a5568;
}
.sso-card.is-success .sso-message { color: #38a169; font-weight: 600; }
.sso-card.is-error .sso-message { color: #e53e3e; font-weight: 600; }

/* ---------- 跳动省略点 ---------- */
.dots {
  display: flex;
  gap: 5px;
  margin-top: 10px;
  height: 8px;
}
.dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #a0aec0;
  animation: bounce 1.2s ease-in-out infinite;
}
.dots span:nth-child(2) { animation-delay: 0.15s; }
.dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}

/* ---------- 底部进度条 ---------- */
.sso-progress {
  width: 100%;
  height: 3px;
  margin-top: 24px;
  overflow: hidden;
  border-radius: 2px;
  background: #edf2f7;
}
.bar { display: block; height: 100%; border-radius: 2px; }
.bar-loading {
  width: 40%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  animation: slide 1.3s ease-in-out infinite;
}
.bar-success { width: 100%; background: #38a169; animation: fill 0.5s ease-out both; }
.bar-error { width: 100%; background: #e53e3e; animation: fill 0.4s ease-out both; }
@keyframes slide {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(260%); }
}
@keyframes fill { from { width: 0; } }

/* ---------- 重试按钮 ---------- */
.sso-retry { margin-top: 18px; }

/* ---------- 过渡 ---------- */
.icon-swap-enter-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.icon-swap-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.icon-swap-enter { opacity: 0; transform: scale(0.7); }
.icon-swap-leave-to { opacity: 0; transform: scale(0.7); }

.fade-up-enter-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.fade-up-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.fade-up-enter { opacity: 0; transform: translateY(6px); }
.fade-up-leave-to { opacity: 0; transform: translateY(-6px); }

/* ---------- 无障碍：尊重系统减弱动效设置 ---------- */
@media (prefers-reduced-motion: reduce) {
  .bg-blob, .spinner-ring, .spinner-orbit, .pulse-halo,
  .dots span, .bar-loading, .sso-card {
    animation: none !important;
  }
  .bar-loading { width: 100%; }
}
</style>
